import {
  getV1CompaniesCompanyIdDrivers,
  patchV1CompaniesCompanyIdUsersId,
  postV1CompaniesCompanyIdUsers,
  TimeZone,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { Driver } from '~hooks/useDrivers/models';
import { User } from '~hooks/useUsers/models';
import { CSVColumn } from '~utils/csv/csvProcessor';
import { getConsolidatedEquipmentName } from '~utils/drivers/driver-utils';
import { fetchAllApiPages } from '~utils/fetchAllPages';
import {
  SentryIgnoreRule,
  withSentryIgnoreRules,
} from '~utils/sentry/sentry-reporting-utils';
import { toE164PhoneNumber } from '~utils/utilFunctions';

import { getPhoneValidationErrors } from './phoneValidationErrors';
import { EntityCSVConfig } from './types';

const driversColumns: CSVColumn[] = [
  {
    key: 'id',
    header: 'id',
    required: false,
    exampleValue: '',
    description: 'Existing driver ID (for updates only)',
  },
  {
    key: 'first_name',
    header: 'first_name',
    required: true,
    exampleValue: 'John',
    description: 'First name',
  },
  {
    key: 'last_name',
    header: 'last_name',
    required: true,
    exampleValue: 'Doe',
    description: 'Last name',
  },
  {
    key: 'equipment',
    header: 'equipment',
    required: false,
    exampleValue: 'TRK-101',
    description: 'Default truck or equipment assignment (export only; ignored on import)',
  },
  {
    key: 'email',
    header: 'email',
    required: false,
    exampleValue: 'john.doe@example.com',
    description: 'Email (required if no phone)',
  },
  {
    key: 'phone',
    header: 'phone',
    required: false,
    exampleValue: '+14155551234',
    description:
      'US numbers like 555-555-5555 or (555) 555-5555; other countries start with + and the country code (e.g., +14155551234) — required if no email',
  },
  {
    key: 'time_zone',
    header: 'time_zone',
    required: true,
    exampleValue: 'America/New_York',
    description: 'IANA timezone (see reference below)',
  },
  {
    key: 'external_id',
    header: 'external_id',
    required: false,
    exampleValue: 'DRV-001',
    description: 'Your internal reference ID',
  },
  {
    key: 'status',
    header: 'status',
    required: false,
    enumValues: ['ACTIVE', 'INACTIVE'],
    exampleValue: 'ACTIVE',
    description: 'ACTIVE or INACTIVE',
  },
  {
    key: 'hours_of_service',
    header: 'hours_of_service',
    required: false,
    enumValues: ['INTRASTATE', 'INTERSTATE'],
    exampleValue: 'INTRASTATE',
    description: 'Hours-of-service rules; leave blank to keep the current value',
  },
  {
    key: 'notes',
    header: 'notes',
    required: false,
    exampleValue: '',
    description: 'Driver notes (max 350 chars)',
  },
  {
    key: 'address',
    header: 'address',
    required: false,
    exampleValue: '',
    description: 'Driver home address',
  },
];

// A missing or blank column sends undefined so the API keeps the current value
const parseIntrastateHoursOfService = (value: string | undefined) => {
  const normalized = value?.trim().toUpperCase();

  return normalized ? normalized === 'INTRASTATE' : undefined;
};

export const createDriversConfig = (
  getRoleIdByName: (name: string) => string | undefined,
): EntityCSVConfig<User, Driver> => ({
  entityType: 'drivers',
  columns: driversColumns,

  fetchAll: (companyId: string) =>
    fetchAllApiPages(
      (query) =>
        getV1CompaniesCompanyIdDrivers({
          path: { 'company-id': companyId },
          query,
        }),
      Driver.parse,
    ),

  create: async (companyId: string, data: Record<string, string>): Promise<User> => {
    // Always use DRIVER role - no need to read from CSV
    const driverRoleId = getRoleIdByName('DRIVER');
    if (!driverRoleId) {
      throw new Error(t('csv_import_export.errors.driver_role_not_found'));
    }
    const roleIds = [driverRoleId];

    const response = await postV1CompaniesCompanyIdUsers({
      path: { 'company-id': companyId },
      body: {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email || undefined,
        phone: toE164PhoneNumber(data.phone),
        time_zone: data.time_zone as TimeZone,
        external_id: data.external_id || undefined,
        iam_role_ids: roleIds,
        intrastate_hours_of_service: parseIntrastateHoursOfService(data.hours_of_service),
        send_invite: {
          email: Boolean(data.email),
          sms: Boolean(data.phone && !data.email),
        },
      },
    });

    return User.parse(response.data.data);
  },

  update: async (
    id: string,
    data: Record<string, string>,
    companyId: string,
  ): Promise<User> => {
    // Always use DRIVER role - no need to read from CSV
    const driverRoleId = getRoleIdByName('DRIVER');
    if (!driverRoleId) {
      throw new Error(t('csv_import_export.errors.driver_role_not_found'));
    }
    const roleIds = [driverRoleId];

    // The id comes straight from the uploaded CSV, so a 404 is expected user data
    // (row numbers instead of Tread IDs, shared drivers, stale exports) and is already
    // surfaced per-row by the importer — suppress it in Sentry (HORIZON-FE-31H).
    const response = await withSentryIgnoreRules(
      SentryIgnoreRule.IGNORE_USER_UPDATE_404,
      () =>
        patchV1CompaniesCompanyIdUsersId({
          path: { 'company-id': companyId, id },
          body: {
            first_name: data.first_name,
            last_name: data.last_name,
            email: data.email || undefined,
            phone: toE164PhoneNumber(data.phone),
            time_zone: data.time_zone as TimeZone,
            external_id: data.external_id || undefined,
            iam_role_ids: roleIds,
            intrastate_hours_of_service: parseIntrastateHoursOfService(
              data.hours_of_service,
            ),
          },
        }),
    );

    return User.parse(response.data.data);
  },

  transformExport: (driver: Driver): Record<string, string> => {
    const isShared = Boolean(driver.companyShare);
    const scheduleOn = isShared
      ? Boolean(driver.companyShare?.scheduleOn)
      : driver.scheduleOn;
    // Driver.phone returns a libphonenumber-formatted string like "+1 415 555 1234";
    // strip spaces and punctuation so the export round-trips through the import path,
    // which forwards the value to the API and expects digits-only / E.164.
    const rawPhone = (driver.phone || '').replace(/[^\d+]/g, '');

    return {
      id: driver.id,
      first_name: driver.firstName,
      last_name: driver.lastName,
      equipment: getConsolidatedEquipmentName(driver) ?? '',
      email: driver.email || '',
      phone: rawPhone,
      time_zone: driver.timeZone || '',
      external_id: driver.externalId || '',
      status: scheduleOn ? 'ACTIVE' : 'INACTIVE',
      hours_of_service: driver.intrastateHoursOfService ? 'INTRASTATE' : 'INTERSTATE',
      notes: '',
      address: '',
    };
  },

  transformImport: (row: Record<string, string>): Record<string, unknown> => ({
    first_name: row.first_name?.trim(),
    last_name: row.last_name?.trim(),
    email: row.email?.trim() || null,
    phone: row.phone?.trim() || null,
    time_zone: row.time_zone?.trim(),
    external_id: row.external_id?.trim() || null,
  }),

  validateRow: (row: Record<string, string>): string[] => {
    const errors: string[] = [];

    if (!row.first_name?.trim()) {
      errors.push(t('csv_import_export.errors.required_field', { field: 'first_name' }));
    }
    if (!row.last_name?.trim()) {
      errors.push(t('csv_import_export.errors.required_field', { field: 'last_name' }));
    }
    if (!row.email?.trim() && !row.phone?.trim()) {
      errors.push(t('csv_import_export.errors.email_or_phone_required'));
    }
    errors.push(...getPhoneValidationErrors(row.phone, 'phone'));
    if (!row.time_zone?.trim()) {
      errors.push(t('csv_import_export.errors.required_field', { field: 'time_zone' }));
    }
    if (row.notes && row.notes.length > 350) {
      errors.push(t('csv_import_export.errors.number_max', { field: 'notes', max: 350 }));
    }

    return errors;
  },
});
